"use client";

import type { NudgeChannel } from "@tillix/db/enums";
import { computed, observable, reaction } from "mobx";
import {
  type Frozen,
  fromSnapshot,
  isoStringToDateTransform,
  Model,
  type ModelCreationData,
  modelAction,
  prop,
} from "mobx-keystone";
import { getStoreConfig, setStoreConfig } from "../config";
import { sessionUserToUserModelSnapshot } from "../utils/db-to-snapshot";
import { InvoiceStore } from "./core/invoice/store.invoice";
import { MeterStore } from "./core/meter/store.meter";
import { MeterReadingStore } from "./core/meter/store.meter-reading";
import "./core/nudge/model.nudge";
import { NudgeStore } from "./core/nudge/store.nudge";
import OrganizationModel from "./core/organization/model.organization";
import {
  AutoPayEnrollmentStore,
  PaymentMethodStore,
  PaymentStore,
} from "./core/payment/store.payment";
import { ServiceAccountStore } from "./core/service-account/store.service-account";
import TilliXStore from "./core/tillix";
import { userCtx } from "./core/user/context.user";
import UserModel, { UserOnboardingModel } from "./core/user/model.user";
import { UserContactStore } from "./core/user/store.user-contact";
import { UninitializedError } from "./lib/error";
import { nsModel } from "./lib/namespace";
import { createGlobalStore, type GlobalStore } from "./stores/global";
import type { StorageAdapter } from "../storage/adapter";
import type { DataTransformer } from "../storage/transformer";
import type { TenantStore } from "./lib/models/tenant-store";
import type { AuthStore } from "./stores/auth";

const rootNs = nsModel("root");

/**
 * Note: we specifically don't extend this from any models so we can safely use things like `instanceof` to check if X is a root store and so that we don't have to deal with any random side effects
 */
@rootNs.store
export default class RootStore extends Model({
  auth: prop<AuthStore>(),
  global: prop<GlobalStore>(() => createGlobalStore()),
  x: prop<TilliXStore | null>(null),
  tenant: prop<TenantStore>(),
  lastSync: prop<string | null>().withTransform(isoStringToDateTransform()),
  onboarding: prop<UserOnboardingModel | undefined>(undefined),
}) {
  // warning: dont touch this
  @observable storageAdapterChangeKey = 0;
  @observable xCreating = false;
  @observable xInitialized = false;

  @modelAction
  private setLastSync(date?: Date) {
    this.lastSync = date ?? new Date();
  }

  @modelAction
  setOnboarding(data: { complete: boolean; step: string }) {
    this.onboarding = new UserOnboardingModel(data);
  }

  @modelAction
  clearOnboarding() {
    this.onboarding = undefined;
  }

  @computed
  get tilliXInitted() {
    return !!this.x;
  }

  /**
   * @throws {UnitializedError} if the tilliX store has not been initialized yet
   */
  @computed
  get tilliX() {
    if (!this.x) {
      throw new UninitializedError("TilliXStore");
    }
    return this.x;
  }

  /**
   * WARNING: you should not be calling this method outside of the tx-core package
   *
   * Creates the TilliXStore with empty child stores. Data is hydrated lazily
   * on the client via the `useHydrate*` hooks. A placeholder `UserModel` is
   * seeded from the session so that consumers depending on `tilliX.user` have
   * something to read before `useHydrateUser()` runs.
   */
  @modelAction
  createTilliXStore(sessionUser?: {
    email: string;
    name?: string | null;
    phoneNumber?: string | null;
  }) {
    if (this.xInitialized || this.xCreating) {
      return;
    }

    this.xCreating = true;

    const invoiceStore = new InvoiceStore({ lastSync: new Date() });
    const paymentMethodsStore = new PaymentMethodStore({
      lastSync: new Date(),
    });
    const autoPayEnrollmentsStore = new AutoPayEnrollmentStore({
      lastSync: new Date(),
    });
    const paymentsStore = new PaymentStore({ lastSync: new Date() });
    const serviceAccountStore = new ServiceAccountStore({
      lastSync: new Date(),
    });
    const contactStore = new UserContactStore({ lastSync: new Date() });
    const meterStore = new MeterStore({ lastSync: new Date() });
    const meterReadingStore = new MeterReadingStore({ lastSync: new Date() });
    const notificationStore = new NudgeStore({
      allPreferences: [],
      lastSync: new Date(),
    });

    // Placeholder user from session — will be overwritten by useHydrateUser()
    const userSnapshot = sessionUserToUserModelSnapshot({
      email: sessionUser?.email ?? this.auth.email ?? "",
      name: sessionUser?.name ?? null,
      phoneNumber: sessionUser?.phoneNumber ?? null,
    });

    this.x = new TilliXStore({
      invoices: invoiceStore,
      meters: meterStore,
      meterReadings: meterReadingStore,
      paymentMethods: paymentMethodsStore,
      payments: paymentsStore,
      autoPayEnrollments: autoPayEnrollmentsStore,
      serviceAccounts: serviceAccountStore,
      contacts: contactStore,
      user: fromSnapshot(UserModel, userSnapshot),
      organization: new OrganizationModel({
        lastSync: new Date(),
      }),
      notifications: notificationStore,
    });

    this.afterCreateXStore();
    this.xCreating = false;
    this.xInitialized = true;
  }

  @modelAction
  private afterCreateXStore() {
    userCtx.setComputed(this, () => {
      return this.tilliX.user;
    });
  }

  @modelAction
  private setAuthStore(authStore: AuthStore) {
    this.auth = authStore;
  }

  @modelAction
  private clearX() {
    // TODO: there should be cleanup logic that flushes the store to disk/ removes from disk if needed, etc
    this.x = null;
  }

  protected override onAttachedToRootStore(): (() => void) | void {
    let callbackId: number | null = null;

    const disposer = reaction(
      () => this.lastSync,
      (snap) => {
        const timeString = snap ? snap.getTime().toString() : "NULL";
        void getStoreConfig()
          .storageAdapter.setString("ROOT", "SYNC", timeString)
          .finally(() => {
            callbackId = null;
          });
      },
      {
        fireImmediately: true,
        scheduler: (run) => {
          if (callbackId) {
            cancelIdleCallback(callbackId);
          }
          callbackId = requestIdleCallback(run);
        },
      },
    );

    return () => {
      if (callbackId) {
        cancelIdleCallback(callbackId);
      }
      disposer();
    };
  }

  protected override async onInit() {
    if (this.auth.isAuthed) {
      this.createTilliXStore();
    }

    // Storage adapter is browser-only — skip on server.
    if (typeof window === "undefined") return;

    void getStoreConfig()
      .storageAdapter.getString("ROOT", "SYNC")
      .then((snap) => {
        if (snap && snap !== "NULL") {
          const epoch = parseInt(snap, 10);
          if (!Number.isNaN(epoch)) {
            this.setLastSync(new Date(epoch));
          }
        }
      })
      .catch(console.error);
  }

  @modelAction
  public setStorageAdapter<T extends DataTransformer>(
    adapter: StorageAdapter<T>,
  ) {
    this.storageAdapterChangeKey += 1;
    setStoreConfig({
      storageAdapter: adapter,
    });
  }
}

type RawModelCreationData<T> =
  T extends ModelCreationData<infer U>
    ? {
        [K in keyof ModelCreationData<U>]: ModelCreationData<U>[K] extends Frozen<
          infer R
        >
          ? R
          : ModelCreationData<U>[K] extends Date
            ? string
            : ModelCreationData<U>[K];
      }
    : never;

export type RootCreationData = {
  auth: RawModelCreationData<ModelCreationData<AuthStore>>;
  tenant: {
    refId: string;
    supportedNudgeChannels: NudgeChannel[];
  };
};
